import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { MenuItem } from '@headlessui/react';
import Icon from 'flow/components/ui-components/Icon';
import React, { useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import { usePopper } from 'react-popper';
import { twMerge } from 'tailwind-merge';

// Keep the submenu open briefly after the pointer leaves so it survives
// crossing the gap between the parent row and the submenu panel (or a small
// drift off the panel while reading/typing). Re-entering cancels the close.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var CLOSE_DELAY_MS = 250;

// Coordinates sibling submenus so that only one is open at a time. Without
// this, moving from one submenu row to a sibling leaves the first panel
// lingering for CLOSE_DELAY_MS, so both panels briefly render stacked on top
// of each other. Opening a submenu closes the previously open sibling
// immediately (bypassing the close delay).

var createNestedMenuGroup = function createNestedMenuGroup() {
  var active = null;
  return {
    activate: function activate(id, closeNow) {
      if (active && active.id !== id) {
        active.closeNow();
      }
      active = {
        id: id,
        closeNow: closeNow
      };
    },
    deactivate: function deactivate(id) {
      if (active && active.id === id) {
        active = null;
      }
    }
  };
};

// The default group is shared by every top-level NestedMenuItem that isn't
// wrapped in a nested group — i.e. the direct submenu rows of a context menu.
// Each NestedMenuItem provides a fresh group to its own children, so a nested
// submenu opening never closes its parent.
var NestedMenuGroupContext = /*#__PURE__*/React.createContext(createNestedMenuGroup());
var NestedMenuItem = function NestedMenuItem(_ref) {
  var children = _ref.children,
    iconKey = _ref.iconKey,
    title = _ref.title,
    _ref$placementDesired = _ref.placementDesired,
    placementDesired = _ref$placementDesired === void 0 ? 'right' : _ref$placementDesired,
    _ref$offset = _ref.offset,
    offset = _ref$offset === void 0 ? [0, 0] : _ref$offset,
    className = _ref.className,
    header = _ref.header,
    listClassName = _ref.listClassName,
    onOpenChange = _ref.onOpenChange,
    onClick = _ref.onClick;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    referenceElement = _useState2[0],
    setReferenceElement = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    popperElement = _useState4[0],
    setPopperElement = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isMenuOpen = _useState6[0],
    setIsMenuOpen = _useState6[1];
  var closeTimeoutRef = useRef();
  var id = useId();
  // The group of the parent menu this row belongs to (siblings coordinate).
  var group = useContext(NestedMenuGroupContext);
  // A fresh group for this submenu's own children, kept stable across renders.
  var childGroup = useMemo(function () {
    return createNestedMenuGroup();
  }, []);
  var cancelScheduledClose = function cancelScheduledClose() {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  };

  // Close without the grace delay. Used both when leaving the menu entirely
  // and when a sibling submenu forces this one shut via the group.
  var closeNow = function closeNow() {
    cancelScheduledClose();
    setIsMenuOpen(false);
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
  };
  var openMenu = function openMenu() {
    cancelScheduledClose();
    // Closes any sibling that is still open, so the panels never stack.
    group.activate(id, closeNow);
    setIsMenuOpen(true);
    onOpenChange === null || onOpenChange === void 0 || onOpenChange(true);
  };
  var scheduleClose = function scheduleClose() {
    cancelScheduledClose();
    closeTimeoutRef.current = setTimeout(function () {
      setIsMenuOpen(false);
      onOpenChange === null || onOpenChange === void 0 || onOpenChange(false);
      group.deactivate(id);
    }, CLOSE_DELAY_MS);
  };

  // Clear any pending close timer and drop out of the group on unmount, to
  // avoid setting state or closing a stale reference after the component is
  // gone.
  useEffect(function () {
    return function () {
      cancelScheduledClose();
      group.deactivate(id);
    };
  }, [group, id]);
  var placement = getPlacementLeftRightFlippedIfDocumentIsRtl(placementDesired);
  var _usePopper = usePopper(referenceElement, popperElement, {
      placement: placement,
      modifiers: [{
        name: 'offset',
        options: {
          offset: offset
        }
      }, {
        name: 'flip',
        enabled: true,
        options: {
          flipVariations: true,
          allowedAutoPlacements: ['auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end']
        }
      }, {
        name: 'preventOverflow',
        enabled: true
      }]
    }),
    styles = _usePopper.styles,
    attributes = _usePopper.attributes;
  return /*#__PURE__*/_jsx(MenuItem, {
    as: 'div',
    onClick: onClick,
    ref: setReferenceElement,
    className: "group flex flex-1 cursor-pointer items-center rounded-sm hover:bg-gray-200",
    onMouseEnter: openMenu,
    onMouseLeave: scheduleClose,
    children: /*#__PURE__*/_jsxs("div", {
      className: "flex flex-1 cursor-pointer items-center rounded-sm p-2 hover:bg-gray-200 dark:hover:bg-gray-300",
      children: [iconKey && /*#__PURE__*/_jsx(Icon, {
        iconKey: iconKey,
        className: "me-2 text-gray-600",
        size: '24px'
      }), /*#__PURE__*/_jsx("span", {
        className: "flex-1",
        children: title
      }), /*#__PURE__*/_jsx("div", {
        className: "arrow",
        children: /*#__PURE__*/_jsx(Icon, {
          iconKey: "chevron_right",
          className: "text-gray-600 rtl:scale-x-[-1]",
          size: '24px'
        })
      }), isMenuOpen && /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
        ref: setPopperElement,
        style: styles.popper
      }, attributes.popper), {}, {
        children: /*#__PURE__*/_jsxs("div", {
          className: twMerge('shadow-base end-2 z-50 mx-3 min-w-[200px] rounded-sm bg-white p-2 dark:bg-gray-200', className),
          children: [header, /*#__PURE__*/_jsx("div", {
            className: twMerge('max-h-[270px] overflow-y-auto', listClassName),
            children: /*#__PURE__*/_jsx(NestedMenuGroupContext.Provider, {
              value: childGroup,
              children: children
            })
          })]
        })
      }))]
    })
  });
};
var getPlacementLeftRightFlippedIfDocumentIsRtl = function getPlacementLeftRightFlippedIfDocumentIsRtl(placement) {
  if (document.dir !== 'rtl') {
    //Document is not right to left, return placement as is
    return placement;
  }

  //Document direction is Right to left, reverse if placement is of a left/right type

  if (placement === 'right') return 'left';
  if (placement === 'right-start') return 'left-start';
  if (placement === 'right-end') return 'left-end';
  if (placement === 'left') return 'right';
  if (placement === 'left-start') return 'right-start';
  if (placement === 'left-end') return 'right-end';
  return placement;
};
export default NestedMenuItem;